import { useCallback, useEffect, useState } from 'react';
import type { Factor } from '@supabase/supabase-js';
import { getSupabase } from '../../supabaseFactory';
import { toSupabaseError } from '../../utils/supabaseError';

export type MfaFactor = {
  id: string;
  /**
   * Fall 9 des Plans, und der Handtyp war hier ENGER ALS DIE WIRKLICHKEIT:
   * Er stand auf `'totp' | 'phone'`, die Bibliothek kennt aber
   * `'totp' | 'phone' | 'webauthn'`. Ein WebAuthn-Faktor wurde damit als
   * `'totp'` deklariert, obwohl zur Laufzeit `'webauthn'` darin steht.
   *
   * Geweitet statt gefiltert — geprüft, dass das verhaltensneutral ist: alle
   * vier Konsumenten (useMfa:155, MfaSection.tsx:92, AdminMfaChallenge.tsx:46,
   * apps/pass login.tsx:140) vergleichen `=== 'totp'` als FILTER; keiner
   * schaltet erschöpfend über die Union. Ein Filter statt der Weitung wäre
   * dagegen eine Verhaltensänderung gewesen.
   */
  factor_type: Factor['factor_type'];
  status: 'verified' | 'unverified';
  friendly_name: string | null;
  created_at: string;
};

export type EnrollResult = {
  factorId: string;
  qrCode: string; // SVG-Data-URL
  secret: string; // Manual-entry-Code
  uri: string;    // otpauth://...
};

/**
 * MFA-Hook für TOTP-basiertes 2FA (Google Authenticator, Authy, 1Password).
 *
 * Supabase-MFA-Flow:
 *   1. enroll()    → erzeugt unverified Factor + QR-Code
 *   2. confirmEnroll(factorId, code) → User scannt + tippt 1. Code
 *      → Factor wird verified
 *   3. listFactors() → zeigt alle aktiven Factors
 *   4. unenroll(factorId) → entfernt Factor
 *
 * Im Login-Flow:
 *   - Nach signInWithPassword: getAal() prüfen
 *   - Wenn nextLevel='aal2' und currentLevel='aal1' → Challenge nötig
 *   - challenge(factorId) + verify(factorId, challengeId, code) → AAL2-Session
 */
export function useMfa(options?: {
  /**
   * false = Faktoren NICHT beim Mount laden (1.7): auf Login-Flächen
   * gibt es noch keine Session — listFactors würde dort mit
   * AuthSessionMissingError rauschen. Nach signInWithPassword dann
   * manuell refresh() rufen. Default true (Web-Verhalten unverändert).
   */
  auto?: boolean;
}) {
  const auto = options?.auto ?? true;
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  // Mit auto:false lädt beim Mount nichts — loading darf dann nicht
  // dauerhaft true stehen (Konsistenz-Sweep 29.07.).
  const [loading, setLoading] = useState(auto);

  const refresh = useCallback(async () => {
    const supabase = getSupabase();
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) {
        console.error('[useMfa] listFactors error', error);
        setFactors([]);
        return;
      }
      const all: Factor[] = data?.all ?? [];
      setFactors(
        all.map((f) => ({
          id: f.id,
          factor_type: f.factor_type,
          status: f.status,
          friendly_name: f.friendly_name ?? null,
          created_at: f.created_at,
        })),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) void refresh();
  }, [auto, refresh]);

  const enroll = useCallback(
    async (friendlyName: string = 'Authenticator-App'): Promise<EnrollResult> => {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName,
      });
      if (error) throw toSupabaseError(error);
      if (!data) throw new Error('MFA enroll returned no data');
      return {
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        uri: data.totp.uri,
      };
    },
    [],
  );

  const confirmEnroll = useCallback(
    async (factorId: string, code: string) => {
      const supabase = getSupabase();
      // Challenge starten, dann mit code verifizieren
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (chErr) throw toSupabaseError(chErr);
      if (!challenge) throw new Error('Challenge fehlgeschlagen');

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyErr) throw toSupabaseError(verifyErr);

      await refresh();
    },
    [refresh],
  );

  const unenroll = useCallback(
    async (factorId: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw toSupabaseError(error);
      await refresh();
    },
    [refresh],
  );

  /**
   * Login-Challenge: nach erfolgreichem Passwort-Login wird hier der
   * 6-stellige Code abgefragt um auf AAL2 hochzustufen.
   */
  const challengeAndVerify = useCallback(
    async (factorId: string, code: string) => {
      const supabase = getSupabase();
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (chErr) throw toSupabaseError(chErr);
      if (!challenge) throw new Error('Challenge fehlgeschlagen');

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyErr) throw toSupabaseError(verifyErr);
    },
    [],
  );

  const hasVerifiedFactor = factors.some(
    (f) => f.status === 'verified' && f.factor_type === 'totp',
  );

  return {
    factors,
    loading,
    hasVerifiedFactor,
    refresh,
    enroll,
    confirmEnroll,
    unenroll,
    challengeAndVerify,
  };
}

/**
 * Holt den aktuellen Authenticator-Assurance-Level der Session.
 * - aal1 = Passwort only
 * - aal2 = Passwort + 2FA (TOTP)
 *
 * Wenn currentLevel=aal1 und nextLevel=aal2 → User HAT MFA, muss noch challengen.
 * Wenn beide gleich → kein Challenge nötig (entweder kein MFA, oder bereits verified).
 */
export async function getAal(): Promise<{
  currentLevel: 'aal1' | 'aal2' | null;
  nextLevel: 'aal1' | 'aal2' | null;
}> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) {
    return { currentLevel: null, nextLevel: null };
  }
  return {
    currentLevel: (data.currentLevel as 'aal1' | 'aal2' | null) ?? null,
    nextLevel: (data.nextLevel as 'aal1' | 'aal2' | null) ?? null,
  };
}
